import type { ProductHit } from '../../../types/algolia';
import type { SearchQuery, SearchResponse } from '@algolia/client-search';

import { startTransition, useEffect, useMemo, useState } from 'react';

import { useAlgoliaAttributesToRetrieve } from './useAlgoliaAttributesToRetrieve';
import { useAlgoliaClient } from './useAlgoliaClient';
import { useAlgoliaFilters } from './useAlgoliaFilters';
import { PageTypes } from '../../constants';
import { STYLE_VARIANT_ATTRIBUTES_TO_RETRIEVE } from '../../constants/algolia';
import { useAppContext } from '../../context/static/AppContext';
import { useStaticContext } from '../../context/static/StaticContext';
import logger from '../../logger';

type StyleVariantsType = { [key: string]: SearchResponse<ProductHit>['hits'] };

export const useAlgoliaStyleVariants = ({ styleCodes }: { styleCodes: string[] }) => {
  const [styleVariants, setStyleVariants] = useState<StyleVariantsType>({});
  const algoliaClient = useAlgoliaClient();
  const { pageType } = useAppContext();
  // SRP reads `/shop/all` category details only for its SAP CX facets and sorting
  const filters = useAlgoliaFilters({ includeCategoryFilter: pageType !== PageTypes.SEARCH });
  const { configuration: { algoliaProductPrimaryIndexName } = {} } = useStaticContext();
  const attributesToRetrieve = useAlgoliaAttributesToRetrieve(STYLE_VARIANT_ATTRIBUTES_TO_RETRIEVE);

  const styleVariantKeys = useMemo(() => new Set(Object.keys(styleVariants)), [styleVariants]);

  const queries: SearchQuery[] = useMemo(() => {
    if (!algoliaProductPrimaryIndexName) {
      return [];
    }

    return styleCodes
      .filter(styleCode => !styleVariantKeys.has(styleCode))
      .map(styleCode => ({
        type: 'default',
        query: styleCode,
        indexName: algoliaProductPrimaryIndexName.replace(/_style$/, ''),
        // disabled for style variant requests as it breaks the analytics in Algolia Dashboard
        clickAnalytics: false,
        analytics: false,
        hitsPerPage: 100,
        filters: `${filters} AND styleCode:${styleCode}`,
        attributesToRetrieve,
        responseFields: ['hits', 'query'],
        attributesToHighlight: [],
      }));
  }, [attributesToRetrieve, algoliaProductPrimaryIndexName, styleCodes, filters, styleVariantKeys]);

  useEffect(() => {
    (async () => {
      if (algoliaClient && algoliaProductPrimaryIndexName && !!queries.length) {
        try {
          const { results = [] } = await algoliaClient.searchForHits<ProductHit>(
            { requests: queries },
            { cacheable: true }
          );

          if (!results.length) {
            return;
          }

          const data = results.reduce((accumulator, item) => {
            accumulator[item.query] = item.hits;

            return accumulator;
          }, {} as StyleVariantsType);

          startTransition(() => {
            setStyleVariants(current => ({ ...current, ...data })); // NOSONAR
          });
        } catch (error) {
          logger.error(error);
        }
      }
    })();
  }, [algoliaClient, algoliaProductPrimaryIndexName, queries]);

  return styleVariants;
};
